<template>
  <div class="landing" >
    <div class="filters-container">
      <Filters
        v-if="shouldShowFilters"
        :isMobile="isMobile"
        :isFixed="areFiltersFixed"/>

      <div
        v-show="isMobile"
        class="actions-container"
        :class="areFiltersFixed ? 'floating-btn' : ''">
        <button
          class="btn btn-link toggle-menu-btn"
          @click="handleMenuToggle">
          <i class="icon icon-filters-rd xs color-black"></i>
          <p>
            <span v-if="!areFiltersFixed && !areFiltersSelected">{{ translate('prFilterYourSearch') }}</span>
            <span v-else> {{ translate('prFilters') }} </span>
            <span v-if="areFiltersSelected">({{getSelectedFiltersNumber}})</span>
          </p>
        </button>
        <button
          v-if="areFiltersSelected"
          class="btn btn-link clear-filters-btn"
          @click="onClearFilters">
          <p>
            <i class="icon-cross xxs"></i>
            {{ translate('prClearAllFilters') }}
          </p>
        </button>
      </div>
    </div>

    <InfoMessage v-show="getQueryParamError"
      :title="translate('prQueryParamErrorTitle')"
      :description="translate('prQueryParamErrorDescription', undefined, lblClickingHere)"
      icon="icon-filters-rd" />

    <PressReleases
      v-if="!getQueryParamError && pressReleases.length"
      :data="groupedPressReleases"
      :isMobile="isMobile"/>
    <PlaceHolderPressReleases v-if="!getQueryParamError && !pressReleases.length"/>

    <Pagination
      v-if="!getQueryParamError && pagination.totalPages > 1"
      :currentPage="pagination.offset"
      :pageCount="pagination.totalPages"
      :visiblePagesCount="getVisiblePagesCount()"
      class="articles-list__pagination"
      @nextPage="handlePageChange('next')"
      @previousPage="handlePageChange('previous')"
      @loadPage="handlePageChange"/>

  </div>

</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { translate } from 'rhg-vue-common'
import Filters from '@/components/Filters'
import PressReleases from '@/components/PressReleases'
import Pagination from '@/components/Pagination'
import PlaceHolderPressReleases from '@/components/PlaceHolderPressReleases'
import InfoMessage from '@/components/InfoMessage'
import { location } from '@/infrastructure/Configuration'
import { clearQueryParams, capitalize, scrollTop } from '@/infrastructure/Utils'

export default {
  name: 'Landing',

  data: () => {
    return {
      isMobile: null,
      areFiltersFixed: false,
      maxVisiblePages: 8
    }
  },

  components: {
    Filters,
    PressReleases,
    Pagination,
    PlaceHolderPressReleases,
    InfoMessage
  },
  async mounted () {
    history.scrollRestoration = 'manual'

    const sessionScrollPosition = sessionStorage.getItem('scroll-position')
    const sessionPagination = JSON.parse(sessionStorage.getItem('pagination'))

    if (sessionPagination) {
      await this.$store.commit('setPagination', sessionPagination)
      sessionStorage.removeItem('pagination')
    }

    await this.$store.dispatch('getFilters')
    await this.$store.dispatch('getTopics')
    await this.$store.dispatch('getAreas')
    await this.$store.dispatch('getPressReleases')

    this.resizeHandler()
    window.addEventListener('resize', this.resizeHandler)
    document.addEventListener('scroll', this.setFilterDistanceFromTop)

    if (sessionScrollPosition) {
      window.scrollTo(0, sessionScrollPosition)
      sessionStorage.removeItem('scroll-position')
    }

    const clickHereBtn = document.querySelector('.click-here-btn')
    clickHereBtn && clickHereBtn.addEventListener('click', this.onClick)
  },

  methods: {
    async handlePageChange (value) {
      switch (value) {
        case 'next':
          this.$store.commit('setPaginationOffset', this.pagination.offset + 1)
          break
        case 'previous':
          this.$store.commit('setPaginationOffset', this.pagination.offset - 1)
          break
        default:
          this.$store.commit('setPaginationOffset', value)
          break
      }
      await this.$store.dispatch('getPressReleases')

      scrollTop()
    },

    resizeHandler () {
      this.isMobile = document.body.clientWidth < 980
      this.$nextTick(() => this.setFilterDistanceFromTop())
    },

    getVisiblePagesCount () {
      const { pagination, maxVisiblePages } = this

      return pagination.totalPages > maxVisiblePages ? maxVisiblePages : pagination.totalPages
    },

    handleMenuToggle () {
      this.$store.commit('toggleMobileMenu')

      this.isMobileMenuToggled ? document.documentElement.style.overflow = 'hidden' : document.documentElement.style.overflow = 'auto'
    },

    onClearFilters () {
      this.$store.commit('clearFilters')
      clearQueryParams()
      this.$store.dispatch('getPressReleases')
      this.$root.$emit('clear-filters')
      scrollTop()
    },

    async onClick () {
      await this.$store.dispatch('setParamsErrorStatus', false)
      await this.$store.commit('clearFilters')
      clearQueryParams()
      await this.$store.dispatch('getPressReleases')
      scrollTop()
    },

    setFilterDistanceFromTop () {
      const element = document.querySelector('.filters-container')
      const distanceFromTop = element && element.getBoundingClientRect().top
      this.areFiltersFixed = window.pageYOffset + distanceFromTop < window.pageYOffset
    },
    translate
  },

  computed: {
    ...mapState(['pressReleases', 'pagination', 'filters', 'isMobileMenuToggled', 'loading']),
    ...mapGetters(['areFiltersSelected', 'getAreaDropdownOptions', 'getTopicDropdownOptions', 'getYearDropdownOptions', 'getSelectedFiltersNumber', 'getQueryParamError']),
    groupedPressReleases () {
      return this.pressReleases.reduce((acc, current) => {
        const dateObj = new Date(current.date)
        let monthYear = dateObj.toLocaleString(location, { month: 'long', year: 'numeric' })
        monthYear = capitalize(monthYear)
        !acc[monthYear]
          ? acc[monthYear] = { monthYear, entries: [current] }
          : acc[monthYear].entries = [...acc[monthYear].entries, current]
        return acc
      }, {})
    },
    lblClickingHere () {
      return '<button class="btn btn-link click-here-btn">' + this.translate('prClickingHere') + '</button>'
    },
    shouldShowFilters () {
      const isMobileIsInitialized = this.isMobile === true || this.isMobile === false
      const areFiltersContentReady = this.getAreaDropdownOptions.length && this.getTopicDropdownOptions.length && this.filters.years.length

      return isMobileIsInitialized && areFiltersContentReady
    }

  },

  beforeDestroy () {
    window.removeEventListener('resize', this.resizeHandler)
  }
}
</script>

<style lang="scss" scoped>
  .landing {
    text-align: right;
    margin-bottom: 40px;
  }

  .filters-container {
    min-height: 40px;
    @media screen and (min-width: 991px) {
      min-height: 135px;
      margin-top: 1rem;
    }
  }

  .actions-container {
    border: 1px solid #E0E0E0;
    border-radius: 500px;
    display: inline-flex;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 10px;

    &.floating-btn {
      background-color: white;
      position: fixed;
      right: 15px;
      bottom: 24px;
      z-index: 2;
      margin-bottom: initial;

      .toggle-menu-btn {
        flex-direction: column;
        align-items: center;

        .icon {
          margin: 0;
        }
      }
    }

    p {
      margin: 0;
      font-size: 12px;
    }

    .toggle-menu-btn {
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
      padding: 8px 16px;
      .icon {
        width: 16px;
        height: 16px;
        line-height: 22px;
        font-size: 16px;
        margin-left: 8px;
        margin-right: 0;
        transition: none;
      }
      .icon-right {
        font-size: 10px;
        line-height: 12px;
        margin-right: 4px;
        margin-left: 0;
      }
      ~ .btn {
        height: 20px;
        border-left: 1px solid #B9B9B9;
        border-radius: 0;
        .icon-cross {
          font-size: 10px;
          line-height: 12px;
          margin-right: 4px;
        }
      }
    }

    .clear-filters-btn {
      padding: 0 16px;
      margin-left: 0px;
    }
  }
</style>
